import { spacing } from 'aos-ds/src/tokens/spacing'
import React, { useCallback, useMemo, useState } from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { useMeasuredWidth } from '../../useMeasuredWidth'
import { collapseExhausted, collapsePlan } from './collapsePlan'
import { FilterSwitchCollapsed } from './FilterSwitchCollapsed'
import { FilterSwitchExpanded } from './FilterSwitchExpanded'
import { FilterSwitchMeasure, type Form } from './FilterSwitchMeasure'
import { FilterSwitchTrigger } from './FilterSwitchTrigger'
import type { FilterSwitchRowProps } from './types'

export const ROW_CLASS = 'flex-row gap-l'
const ROW_GAP = spacing['medium-less-dense/l']

type Widths = Record<string, Partial<Record<Form, number>>>

/**
 * A row of filter switches (Figma FilterSwitch `6868:70945`, FilterSwitch/Collapsed
 * `17437:90999`, FilterSwitch/Trigger `17294:234967`, Filter `6868:70896`).
 *
 * Each switch shows its pills while they fit and its collapsed trigger otherwise;
 * `collapsePlan` decides which, and the row wraps only once `collapseExhausted`
 * says collapsing has nothing left to give. Both forms of every switch are rendered once,
 * out of sight, to be measured. A collapsed switch opens its list in a portal, so
 * the list is not clipped by an `overflow-hidden` tile, and only one list is open
 * at a time.
 */
export const FilterSwitchRow = ({
    groups,
    className,
    testID = 'filter-switch-row',
}: FilterSwitchRowProps) => {
    const row = useMeasuredWidth()
    const [widths, setWidths] = useState<Widths>({})
    const [openKey, setOpenKey] = useState<string | null>(null)

    const report = useCallback((groupKey: string, form: Form, width: number) => {
        setWidths(previous =>
            previous[groupKey]?.[form] === width
                ? previous
                : { ...previous, [groupKey]: { ...previous[groupKey], [form]: width } },
        )
    }, [])

    const plan = useMemo(() => {
        const measured = groups.map(group => ({
            expanded: widths[group.key]?.expanded ?? 0,
            collapsed: widths[group.key]?.collapsed ?? 0,
        }))
        const collapsed = collapsePlan(measured, row.width, ROW_GAP)
        return { collapsed, wrap: collapseExhausted(measured, collapsed) }
    }, [groups, widths, row.width])

    return (
        <View {...row.props} className={twMerge('w-full', className)} testID={testID}>
            <View
                className={twMerge(ROW_CLASS, plan.wrap ? 'flex-wrap' : 'flex-nowrap')}
                testID={`${testID}-switches`}
            >
                {groups.map((group, index) =>
                    plan.collapsed[index] ? (
                        <FilterSwitchCollapsed
                            key={group.key}
                            group={group}
                            open={openKey === group.key}
                            onOpenChange={open => setOpenKey(open ? group.key : null)}
                            testID={`${testID}-${group.key}`}
                        />
                    ) : (
                        <FilterSwitchExpanded
                            key={group.key}
                            group={group}
                            testID={`${testID}-${group.key}`}
                        />
                    ),
                )}
            </View>
            <View
                className='pointer-events-none absolute left-0 top-0 h-0 w-0 flex-row items-start overflow-hidden'
                aria-hidden
                testID={`${testID}-measure`}
            >
                {groups.map(group => (
                    <React.Fragment key={group.key}>
                        <FilterSwitchMeasure
                            groupKey={group.key}
                            form='expanded'
                            onWidth={report}
                            testID={`${testID}-measure-${group.key}-expanded`}
                        >
                            <FilterSwitchExpanded
                                group={group}
                                testID={`${testID}-mirror-${group.key}`}
                                measureOnly
                            />
                        </FilterSwitchMeasure>
                        <FilterSwitchMeasure
                            groupKey={group.key}
                            form='collapsed'
                            onWidth={report}
                            testID={`${testID}-measure-${group.key}-collapsed`}
                        >
                            <FilterSwitchTrigger
                                group={group}
                                open={false}
                                testID={`${testID}-mirror-${group.key}-face`}
                            />
                        </FilterSwitchMeasure>
                    </React.Fragment>
                ))}
            </View>
        </View>
    )
}

FilterSwitchRow.displayName = 'FilterSwitchRow'
